import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { getStoredAppLanguage } from '@/lib/language';

// Minimal Serbian translations for login + share pages (loaded immediately)
const defaultTranslations = {
  'sr-RS': {
    translation: {
      common: {
        notifications: 'Obaveštenja',
        status: { loading: 'Učitavanje...' }
      },
      chat: {
        disclaimer: 'FONGPT može da pogreši. Proverite važne informacije.'
      },
      auth: {
        login: {
          title: 'Prijavite se za pristup aplikaciji',
          subtitle: 'Izaberite način prijave.',
          terms: 'Uslovi korišćenja',
          privacy: 'Privatnost',
          form: {
            email: {
              label: 'Email adresa',
              required: 'Email adresa je obavezna.'
            },
            password: {
              label: 'Lozinka',
              required: 'Lozinka je obavezna.'
            },
            actions: {
              signin: 'Prijava',
              signingIn: 'Prijavljivanje…'
            },
            alternativeText: {
              or: 'ILI'
            }
          },
          errors: {
            default: 'Nije moguće prijaviti se',
            signin: 'Pokušajte da se prijavite pomoću drugog naloga.',
            oauthSignin: 'Pokušajte da se prijavite pomoću drugog naloga.',
            credentialsSignin:
              'Prijava nije uspela. Proverite tačnost unetih podataka.',
            sessionRequired:
              'Molimo vas da se prijavite da biste pristupili ovoj stranici'
          }
        },
        provider: {
          continue: 'Nastavi preko {{provider}} naloga',
          continueGoogle: 'Nastavi preko Google naloga',
          continueFon: 'Nastavi preko FON naloga'
        }
      },
      sharePage: {
        badge: 'Podeljeni razgovor',
        subtitle:
          'Snimak razgovora namenjen samo za čitanje. Ponovo otvorite link da biste videli novije izmene.',
        missing: 'Nedostaje link za deljenje.',
        invalid: 'Ovaj link nije važeći ili je povučen.',
        loadFailed: 'Nije moguće učitati podeljeni razgovor.',
        viewOnly:
          'Ova stranica je samo za pregled. Prijava nije potrebna za podeljene razgovore.'
      },
      threadHistory: {
        thread: {
          menu: {
            share: 'Podeli'
          },
          actions: {
            share: {
              title: 'Podeli razgovor',
              description:
                'Svako ko ima link može da pregleda snimak ovog razgovora, bez mogućnosti izmene. Razgovor ostaje privatan dok ne napravite link.',
              linkLabel: 'Link',
              loading: 'Učitavanje…',
              empty:
                'Još nema linka — napravite ga da biste podelili razgovor.',
              stop: 'Prekinite deljenje',
              close: 'Zatvorite',
              copy: 'Kopirajte link',
              create: 'Napravite link',
              createdCopied: 'Link za deljenje je napravljen i kopiran',
              created: 'Link za deljenje je napravljen',
              copied: 'Link je kopiran',
              copyFailed: 'Nije moguće kopirati link',
              stopped: 'Deljenje je zaustavljeno',
              errors: {
                status: 'Nije moguće učitati status deljenja',
                open: 'Nije moguće otvoriti prozor za deljenje',
                create: 'Nije moguće napraviti link za deljenje',
                stop: 'Nije moguće zaustaviti deljenje'
              }
            }
          }
        }
      }
    }
  },
  'en-US': {
    translation: {
      common: {
        notifications: 'Notifications',
        status: { loading: 'Loading...' }
      },
      chat: {
        disclaimer: 'FONGPT can make mistakes. Check important information.'
      },
      auth: {
        login: {
          title: 'Sign in to access the application',
          subtitle: 'Choose how you want to sign in.',
          terms: 'Terms of Service',
          privacy: 'Privacy',
          form: {
            email: { label: 'Email address', required: 'Email is required' },
            password: { label: 'Password', required: 'Password is required' },
            actions: { signin: 'Sign in', signingIn: 'Signing in…' },
            alternativeText: { or: 'OR' }
          },
          errors: {
            default: 'Unable to sign in',
            signin: 'Try signing in with another account',
            oauthSignin: 'Try signing in with another account',
            credentialsSignin: 'Sign-in failed. Check the details you entered.',
            sessionRequired: 'Please sign in to access this page'
          }
        },
        provider: {
          continue: 'Continue with {{provider}}',
          continueGoogle: 'Continue with Google',
          continueFon: 'Continue with FON'
        }
      },
      welcome: {
        alert: 'Hi! I am FONGPT, your student assistant.',
        greeting: {
          hello: 'Welcome',
          subtitle: 'How can I help you?'
        },
        cards: {
          card1: 'When is the course test?',
          card2: 'Where is lecture hall 3?',
          card3: 'What can you help me with?',
          card4: 'How can I apply for an Erasmus exchange?'
        }
      },
      sharePage: {
        badge: 'Shared conversation',
        subtitle:
          'A read-only snapshot. Open the link again to see newer changes.',
        missing: 'The share link is missing.',
        invalid: 'This share link is invalid or has been revoked.',
        loadFailed: 'The shared conversation could not be loaded.',
        viewOnly:
          'This page is read-only. Sign-in is not required for shared conversations.'
      }
    }
  }
};

const i18nConfig = {
  // Never fall through from English into Serbian (or vice versa).
  fallbackLng: false as const,
  defaultNS: 'translation',
  resources: defaultTranslations,
  lng: getStoredAppLanguage()
};

export function i18nSetupLocalization(): void {
  i18n
    .use(initReactI18next)
    .init(i18nConfig)
    .catch((err) => console.error('[i18n] Failed to setup localization.', err));
}
